"use client";

import { useMemo, useState } from "react";
import Link from "next/link";

type VendorComparison = {
  id: string;
  title: string;
  vendor: string;
  href: string;
  image: string;
  imageAlt: string;
  keywords: string[];
  benefits: string[];
};

const VENDOR_COMPARISONS: VendorComparison[] = [
  {
    id: "dynatrace",
    title: "Middleware vs Dynatrace",
    vendor: "Dynatrace",
    href: "/comparison/dynatrace-alternative/",
    image: "/images/middleware-vs-dynatrace.webp",
    imageAlt: "Middleware vs Dynatrace comparison",
    keywords: ["dynatrace", "apm", "observability"],
    benefits: [
      "90% lower observability costs",
      "No host or user-based pricing",
      "One-minute OpenTelemetry setup",
    ],
  },
  {
    id: "datadog",
    title: "Middleware vs Datadog",
    vendor: "Datadog",
    href: "/comparison/datadog-alternative/",
    image: "/images/middleware-vs-datadog.webp",
    imageAlt: "Middleware vs Datadog comparison",
    keywords: ["datadog", "monitoring", "observability"],
    benefits: [
      "80%+ lower observability costs",
      "99% feature parity",
      "One-minute OpenTelemetry setup",
    ],
  },
  {
    id: "grafana",
    title: "Middleware vs Grafana",
    vendor: "Grafana",
    href: "/comparison/grafana-vs-middleware/",
    image: "/images/middleware-vs-grafana.webp",
    imageAlt: "Middleware vs Grafana comparison",
    keywords: ["grafana", "dashboards", "observability"],
    benefits: [
      "Unified observability platform",
      "Built-in AI-powered troubleshooting",
      "One-minute OpenTelemetry setup",
    ],
  },
  {
    id: "newrelic",
    title: "Middleware vs New Relic",
    vendor: "New Relic",
    href: "/comparison/newrelic-alternative/",
    image: "/images/middleware-vs-newrelic.webp",
    imageAlt: "Middleware vs New Relic comparison",
    keywords: ["newrelic", "new relic", "apm", "observability"],
    benefits: [
      "5× more value at lower cost",
      "Unlimited users on every plan",
      "One-minute OpenTelemetry setup",
    ],
  },
  {
    id: "coralogix",
    title: "Middleware vs Coralogix",
    vendor: "Coralogix",
    href: "/comparison/coralogix-vs-middleware/",
    image: "/images/comparison/middleware-vs-coralogix.webp",
    imageAlt: "Middleware vs Coralogix comparison",
    keywords: ["coralogix", "streama", "logs", "observability"],
    benefits: [
      "No Streama priority-tier billing",
      "OpenTelemetry-native, not Streama-native",
      "One-minute OpenTelemetry setup",
    ],
  },
];

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3.42118 10.3546C3.69273 10.9935 4.08747 11.5728 4.58287 12.0592C5.07827 12.5456 5.66462 12.9296 6.30844 13.1894C6.95227 13.4492 7.64096 13.5797 8.3352 13.5733C9.02944 13.5669 9.71562 13.4239 10.3546 13.1524C10.9935 12.8808 11.5728 12.4861 12.0592 11.9907C12.5456 11.4953 12.9296 10.9089 13.1894 10.2651C13.4492 9.62126 13.5797 8.93256 13.5733 8.23832C13.5669 7.54409 13.4239 6.8579 13.1523 6.21894C12.8808 5.57998 12.486 5.00077 11.9906 4.51436C11.4952 4.02796 10.9089 3.6439 10.2651 3.3841C9.62124 3.1243 8.93255 2.99386 8.23831 3.00022C7.54407 3.00658 6.85789 3.14962 6.21893 3.42117C5.57997 3.69272 5.00076 4.08747 4.51435 4.58287C4.02795 5.07827 3.64389 5.66462 3.38409 6.30844C3.1243 6.95227 2.99386 7.64096 3.00022 8.3352C3.00659 9.02944 3.14963 9.71563 3.42118 10.3546Z"
        stroke="#5B6471"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.0244 12.0254L16.1247 16.1262"
        stroke="#5B6471"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function VendorComparisons() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return VENDOR_COMPARISONS;

    return VENDOR_COMPARISONS.filter((item) => {
      const haystack = [
        item.title,
        item.vendor,
        item.id,
        ...item.keywords,
        ...item.benefits,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  return (
    <section className="vendor-comparisons" aria-labelledby="vendor-comparisons-heading" id="vendor-comparisons">
      <div className="container">
        <div className="vendor-comparisons-layout">
          <aside className="vendor-comparisons-sidebar">
            <div className="vendor-comparisons-intro">
              <span className="vendor-comparisons-badge">Observability platform comparisons</span>
              <h2 id="vendor-comparisons-heading">
                Compare Middleware with leading vendors
              </h2>
              <label className="vendor-comparisons-search">
                <span className="vendor-comparisons-search-icon">
                  <SearchIcon />
                </span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or type"
                  aria-label="Search vendor comparisons"
                />
              </label>
            </div>

            <div className="vendor-comparisons-help">
              <p>
                Don't see your observability platform?
              </p>
              <Link href="/contact-us/" className="read-more">
                Request a Comparison
              </Link>
            </div>
          </aside>

          <div className="vendor-comparisons-grid" role="list">
            {filtered.length > 0 ? (
              filtered.map((item) => (
                <article
                  key={item.id}
                  className="vendor-comparison-card"
                  role="listitem"
                >
                  <div className="vendor-comparison-card-image">
                    <img
                      src={item.image}
                      alt={item.imageAlt}
                      width={334}
                      height={180}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="vendor-comparison-card-body">
                    <h3>{item.title}</h3>
                    <ul className="vendor-comparison-benefits">
                      {item.benefits.map((benefit) => (
                        <li key={benefit}>
                          <img
                            src="/images/circle-mw.svg"
                            alt=""
                            width={16}
                            height={16}
                          />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                    <Link href={item.href} className="read-more">
                      Compare Now
                    </Link>
                  </div>
                </article>
              ))
            ) : (
              <p className="vendor-comparisons-empty">
                No comparisons match &ldquo;{query}&rdquo;. Try another vendor
                name, or{" "}
                <Link href="/contact-us/">contact us</Link>.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
